import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import { GET_ARTICLE_DETAILS } from "api";
import useFetch from "utils/hooks/useFetch";

import { ChevronLeft } from "lucide-react";
// import parse from "html-react-parser";
// import Swal from "sweetalert2";
import moment from "moment";

import { getClientURL } from "server/helpers/get-client-url";
import useToastMessage from "helpers/toast-message";
import ShareUsingSocialMedia from "components/layout/ShareUsingSocialMedia";

const ArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, showInfo } = useToastMessage();
  const { fetch: getArticleDetails } = useFetch(GET_ARTICLE_DETAILS);

  const [articleDetails, setArticleDetails] = useState(null);
  const [activeLanguage, setActiveLanguage] = useState("en");

  const fetchArticleDetails = (article_id) => {
    getArticleDetails({ params: { id: article_id } })
      .then((res) => {
        setArticleDetails(res.response);
      })
      .catch((err) => {
        showError(err?.data?.message);
      });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${getClientURL()}/articles/details/${id}`);
    showInfo("Link has been copied to clipboard");
  };

  useEffect(() => {
    if (id) fetchArticleDetails(id);
  }, [id]);

  return (
    <section>
      <div className="app-main-container">
        <div className="max-w-4xl px-4 py-6 mx-auto">
          <div className="flex items-center justify-between mb-8">
            {/* Back link */}
            <div
              onClick={() => navigate(-1)}
              className="flex items-center text-xs text-blue-600 cursor-pointer sm:text-sm md:text-base group"
            >
              <ChevronLeft
                className="w-3 h-3 mr-1 text-blue-600 sm:w-4 sm:h-4 md:w-5 md:h-5 group-hover:text-black"
                strokeWidth={3}
              />
              <span className="relative after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-black after:transition-all after:duration-300 group-hover:after:w-full group-hover:font-bold group-hover:text-black">
                Back to previous page
              </span>
            </div>

            {/* Language switch */}
            <div>
              <label className="inline-flex items-center mr-3 cursor-pointer sm:mr-5">
                <span className="mr-2 text-base font-bold text-black sm:text-lg md:text-xl">
                  EN
                </span>
                <input type="checkbox" value="" className="sr-only peer" />
                <div
                  onClick={() =>
                    setActiveLanguage(activeLanguage === "en" ? "id" : "en")
                  }
                  className="relative w-[36px] h-5 sm:w-11 sm:h-6 md:w-[52px] md:h-7 
                    peer-focus:outline-none rounded-full peer
                    peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                    peer-checked:after:border-white
                    after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 md:after:h-6 md:after:w-6
                    after:transition-all bg-app-orange"
                />
                <span className="ml-2 text-base font-bold text-black sm:text-lg md:text-xl">
                  ID
                </span>
              </label>
            </div>
          </div>

          {/* Title with shared layout */}
          <motion.h1
            layoutId={`title-${articleDetails?.id}`}
            className="mb-8 text-3xl font-bold leading-snug text-blue-900 md:text-4xl"
          >
            {activeLanguage === "en" && articleDetails?.title_en
              ? articleDetails?.title_en
              : activeLanguage === "id" && articleDetails?.title_idn
              ? articleDetails?.title_idn
              : articleDetails?.title_en || articleDetails?.title_idn}
          </motion.h1>

          <div className="space-y-5 text-sm text-gray-600">
            <p>
              <span className="italic">
                {moment(articleDetails?.createdAt).format("DD MMMM YYYY")}
              </span>{" "}
              – <span className="italic">Article</span>
            </p>

            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 overflow-hidden rounded-full">
                <img
                  src={articleDetails?.author_img_url}
                  alt={`Author-${articleDetails?.author_name}`}
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="px-0.5 rounded">
                <p className="font-medium text-black">
                  Written by <b>{articleDetails?.author_name}</b>
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-5 mb-5 md:justify-end">
            <ShareUsingSocialMedia
              twitterURL={`${getClientURL()}/articles/details/${id}`}
              facebookURL={`${getClientURL()}/articles/details/${id}`}
              linkedinURL={`${getClientURL()}/articles/details/${id}`}
              mailSubject={articleDetails?.title}
              mailContent={`Hello, I recommend you to read the articles on Institute for Sustainability and Agility (ISA): ${
                articleDetails?.title
              } ${getClientURL()}/articles/details/${id}`}
              copyLink={copyLink}
            />
          </div>

          {/* Image with shared layout */}
          <motion.div
            layoutId={`image-${articleDetails?.id}`}
            className="h-[400px] w-full overflow-hidden mb-8"
          >
            <img
              src={articleDetails?.article_img_url}
              alt={`article-${articleDetails?.title}`}
              className="object-cover w-full h-full"
            />
          </motion.div>

          {articleDetails?.content_idn && articleDetails?.content_en && (
            <div
              className="custom-list "
              dangerouslySetInnerHTML={{
                __html:
                  activeLanguage === "en" && articleDetails?.content_en
                    ? articleDetails?.content_en
                    : activeLanguage === "id" && articleDetails?.content_idn
                    ? articleDetails?.content_idn
                    : articleDetails?.content_en || articleDetails?.content_idn,
              }}
            ></div>
          )}

          <div className="flex justify-center mt-5 mb-5 md:justify-start">
            <ShareUsingSocialMedia
              twitterURL={`${getClientURL()}/articles/details/${id}`}
              facebookURL={`${getClientURL()}/articles/details/${id}`}
              linkedinURL={`${getClientURL()}/articles/details/${id}`}
              mailSubject={articleDetails?.title}
              mailContent={`Hello, I recommend you to read the articles on Institute for Sustainability and Agility (ISA): ${
                articleDetails?.title
              } ${getClientURL()}/articles/details/${id}`}
              copyLink={copyLink}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticleDetails;
